/**
 * Week / 2-week schedule view — crew × day load grid.
 *
 * Each cell shows the crew member's assigned hours for that day as a
 * colored bar. Click a cell to jump to the dispatch board day view.
 * Hover reveals a tooltip with the task list for that day.
 *
 * Complements the day dispatch board — this is the "zoom out" for
 * weekly capacity planning.
 */
import { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  addDays,
  format,
  parseISO,
  isToday as dateIsToday,
  isSameDay,
  isWeekend,
} from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Calendar,
  Hammer,
  Truck,
  Wrench,
  Package,
} from 'lucide-react';
import type { ScheduleDayCell, ScheduleCrewRow, TaskType } from '@vari-crew/schemas';
import { useScheduleWeek } from '@/api/schedule';
import { mondayOfWeek } from '@/lib/dates';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

type ViewMode = '5' | '7' | '14';

const TYPE_ICON: Record<TaskType, typeof Truck> = {
  pickup: Truck,
  install: Hammer,
  punch: Wrench,
  labor: Package,
};

export function ScheduleWeek() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState<string>(mondayOfWeek(new Date()));
  const [viewMode, setViewMode] = useState<ViewMode>('7');
  // Collapse the sticky crew column to an initials rail once the grid is
  // scrolled right (a phone otherwise loses a third of the timeline to names).
  const [crewCollapsed, setCrewCollapsed] = useState(false);
  const days = Number(viewMode);
  const { data, isLoading, error } = useScheduleWeek(startDate, days);

  const dateRange = useMemo(() => {
    const dates: Date[] = [];
    const start = parseISO(startDate);
    for (let i = 0; i < days; i++) {
      dates.push(addDays(start, i));
    }
    return dates;
  }, [startDate, days]);

  function shiftWeek(direction: -1 | 1) {
    const current = parseISO(startDate);
    const shifted = addDays(current, direction * days);
    setStartDate(format(shifted, 'yyyy-MM-dd'));
  }

  function goToToday() {
    setStartDate(mondayOfWeek(new Date()));
  }

  function navigateToDay(dateStr: string) {
    navigate(`/dispatch?date=${dateStr}`);
  }

  const isCurrentWeek = startDate === mondayOfWeek(new Date());

  return (
    <div className="p-5 max-w-[1600px] mx-auto space-y-3">
      {/* ---------- Top bar ---------- */}
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Schedule</h1>
          <p className="text-sm text-ink-dim">
            Crew load by day · click a cell to open the day view
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* View mode toggle */}
          <div className="flex border border-line rounded-md overflow-hidden text-xs">
            {(['5', '7', '14'] as ViewMode[]).map((m) => (
              <button
                key={m}
                onClick={() => setViewMode(m)}
                className={cn(
                  'px-3 py-1.5 transition-colors',
                  viewMode === m
                    ? 'bg-ink text-bg font-medium'
                    : 'hover:bg-fill-soft',
                )}
              >
                {m === '5' ? 'Work week' : m === '7' ? 'Week' : '2 weeks'}
              </button>
            ))}
          </div>

          {/* Navigation — grouped so it wraps under the toggle as a unit. */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => shiftWeek(-1)}
              aria-label="Previous"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex-1 text-sm font-medium px-3 py-2 border border-line rounded-md min-w-[150px] sm:min-w-[210px] text-center whitespace-nowrap">
              {format(parseISO(startDate), 'MMM d')} – {format(addDays(parseISO(startDate), days - 1), 'MMM d, yyyy')}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => shiftWeek(1)}
              aria-label="Next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            {!isCurrentWeek && (
              <Button variant="ghost" size="sm" onClick={goToToday}>
                <Calendar className="w-3.5 h-3.5 mr-1" />
                This week
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* ---------- Summary bar ---------- */}
      {data && (
        <div className="flex items-center gap-3 flex-wrap text-xs">
          <span className="text-ink-faint uppercase tracking-wide">
            {days}-day totals
          </span>
          <span className="font-mono text-ink">
            {data.summary.total_assigned_hours}h
            <span className="text-ink-faint"> / {data.summary.total_available_hours}h</span>
          </span>
          <span className="text-ink-dim">
            {data.summary.total_task_count} tasks
          </span>
          {data.summary.unassigned_count > 0 && (
            <span className="px-2 py-0.5 rounded bg-status-scheduled text-status-scheduled-fg">
              {data.summary.unassigned_count} unassigned
            </span>
          )}
        </div>
      )}

      {/* ---------- Loading / error ---------- */}
      {isLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="w-5 h-5 animate-spin inline-block text-ink-faint" />
          </CardContent>
        </Card>
      )}
      {error && (
        <Card>
          <CardContent className="py-12 text-center text-status-blocked-fg text-sm">
            {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {/* ---------- Grid ---------- */}
      {data && (
        <Card>
          <div
            className="overflow-x-auto"
            onScroll={(e) => setCrewCollapsed(e.currentTarget.scrollLeft > 24)}
          >
            <table className="w-full border-collapse min-w-[700px]">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-fill-soft p-0 text-left text-[10px] uppercase tracking-wide text-ink-faint border-b border-line">
                    <div
                      className="transition-[width] duration-200 ease-out overflow-hidden px-3 py-2"
                      style={{ width: crewCollapsed ? 56 : 200 }}
                    >
                      Crew
                    </div>
                  </th>
                  {dateRange.map((d) => {
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                    const today = dateIsToday(d);
                    return (
                      <th
                        key={d.toISOString()}
                        className={cn(
                          'px-1 py-2 text-center text-[10px] border-b border-line min-w-[80px]',
                          isWeekend ? 'bg-fill-soft/50 text-ink-faint' : 'bg-fill-soft',
                          today && 'ring-2 ring-inset ring-status-onsite-fg/40',
                        )}
                      >
                        <button
                          onClick={() => navigateToDay(format(d, 'yyyy-MM-dd'))}
                          className="hover:underline"
                        >
                          <div className="uppercase tracking-wide text-ink-faint">
                            {format(d, 'EEE')}
                          </div>
                          <div className={cn('text-sm font-semibold', today ? 'text-status-onsite-fg' : 'text-ink')}>
                            {format(d, 'MMM d')}
                          </div>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.crew_rows.map((row) => (
                  <CrewWeekRow
                    key={row.crew_member.id}
                    row={row}
                    dateRange={dateRange}
                    onCellClick={navigateToDay}
                    collapsed={crewCollapsed}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="border-t border-line px-4 py-3 flex items-center gap-4 flex-wrap text-xs text-ink-faint">
            <span>Load:</span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm bg-status-complete" /> &lt; 80%
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm bg-status-enroute" /> 80–100%
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm bg-status-blocked" /> &gt; 100%
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm bg-fill-soft border border-line" /> No work
            </span>
            <span className="ml-auto">Click any cell to open the dispatch day view</span>
          </div>
        </Card>
      )}
    </div>
  );
}

/** Avatar ring + text color for the week load band — kept visible when the
 *  crew column collapses (the load signal is the point of the capacity view). */
function loadBand(pct: number, hasTasks: boolean): { ring: string; text: string } {
  if (!hasTasks || pct === 0) return { ring: 'ring-line', text: 'text-ink-faint' };
  if (pct > 100) return { ring: 'ring-status-blocked-fg', text: 'text-status-blocked-fg' };
  if (pct > 80) return { ring: 'ring-status-enroute-fg', text: 'text-status-enroute-fg' };
  return { ring: 'ring-status-complete-fg', text: 'text-status-complete-fg' };
}

function CrewWeekRow({
  row,
  dateRange,
  onCellClick,
  collapsed,
}: {
  row: ScheduleCrewRow;
  dateRange: Date[];
  onCellClick: (date: string) => void;
  collapsed: boolean;
}) {
  const name = row.crew_member.user.display_name;
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  const weekHours = row.days.reduce((s, d) => s + d.assigned_hours, 0);
  // Capacity is a 5-day work week: only weekdays in the visible range count.
  const weekCapacity =
    row.capacity_hours_per_day * dateRange.filter((d) => !isWeekend(d)).length;
  const weekPct = weekCapacity > 0 ? Math.round((weekHours / weekCapacity) * 100) : 0;
  const hours1 = Math.round(weekHours * 10) / 10;
  const band = loadBand(weekPct, weekHours > 0);

  return (
    <tr className="border-t border-line">
      {/* Crew cell — collapses to an initials + load% rail when scrolled. */}
      <td className="sticky left-0 z-10 bg-bg p-0 text-xs align-middle">
        <div
          className="transition-[width] duration-200 ease-out overflow-hidden"
          style={{ width: collapsed ? 56 : 200 }}
        >
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="flex flex-col items-center gap-0.5 shrink-0">
              <div
                title={
                  collapsed
                    ? `${name} · ${row.crew_member.crew_role} · ${hours1}h / ${weekCapacity}h (${weekPct}%)`
                    : undefined
                }
                className={cn(
                  'w-7 h-7 rounded-full bg-fill-soft flex items-center justify-center text-[10px] font-bold',
                  collapsed && `ring-2 ${band.ring}`,
                )}
              >
                {initials}
              </div>
              {collapsed && (
                <span className={cn('text-[9px] font-mono leading-none', band.text)}>
                  {weekPct}%
                </span>
              )}
            </div>
            <div
              className={cn(
                'min-w-0 transition-opacity duration-150',
                collapsed ? 'opacity-0' : 'opacity-100',
              )}
            >
              <div className="font-medium truncate">{name}</div>
              <div className="text-[10px] text-ink-faint">
                {row.crew_member.crew_role}
                <span className="ml-1 font-mono">
                  {hours1}h / {weekCapacity}h
                  <span className={cn('ml-1', weekPct > 100 ? 'text-status-blocked-fg font-semibold' : '')}>
                    ({weekPct}%)
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </td>

      {/* Day cells */}
      {dateRange.map((d) => {
        const dateStr = format(d, 'yyyy-MM-dd');
        const cell = row.days.find((c) => c.date === dateStr);
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        return (
          <td
            key={dateStr}
            className={cn('p-1', isWeekend && 'bg-fill-soft/30')}
          >
            {cell ? (
              <DayCell
                cell={cell}
                capacity={row.capacity_hours_per_day}
                onClick={() => onCellClick(dateStr)}
              />
            ) : (
              <div className="h-12" />
            )}
          </td>
        );
      })}
    </tr>
  );
}

function DayCell({
  cell,
  capacity,
  onClick,
}: {
  cell: ScheduleDayCell;
  capacity: number;
  onClick: () => void;
}) {
  // Direct-project holds + pickup runs count as load alongside tasks (D-3 /
  // workstream C — a crew member on a direct project used to read as free).
  const projectCount = (cell.projects ?? []).length;
  const pickupCount = (cell.pickups ?? []).length;
  const jobCount = cell.task_count + projectCount + pickupCount;
  const pct = capacity > 0 ? (cell.assigned_hours / capacity) * 100 : 0;
  const barColor =
    jobCount === 0
      ? 'bg-fill-soft'
      : pct > 100
        ? 'bg-status-blocked'
        : pct > 80
          ? 'bg-status-enroute'
          : 'bg-status-complete';
  const textColor =
    jobCount === 0 ? 'text-ink-faint' : pct > 100 ? 'text-status-blocked-fg' : 'text-ink';

  const parts = [
    cell.task_count > 0 && `${cell.task_count} task${cell.task_count > 1 ? 's' : ''}`,
    projectCount > 0 && `${projectCount} project${projectCount > 1 ? 's' : ''}`,
    pickupCount > 0 && `${pickupCount} pickup${pickupCount > 1 ? 's' : ''}`,
  ].filter(Boolean) as string[];
  // The cell is tiny — one specific kind names itself, a mix says "N jobs";
  // the tooltip always carries the full breakdown.
  const label = parts.length === 1 ? parts[0] : `${jobCount} job${jobCount > 1 ? 's' : ''}`;

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full h-12 rounded-md border border-line/60 relative overflow-hidden group',
        'hover:ring-2 hover:ring-ink/20 transition-all cursor-pointer',
        jobCount === 0 && 'opacity-60',
      )}
      title={jobCount === 0 ? 'No work' : `${parts.join(' · ')} · ${cell.assigned_hours}h`}
    >
      {/* Load bar background */}
      <div
        className={cn('absolute inset-y-0 left-0 transition-all', barColor)}
        style={{ width: `${Math.min(pct, 100)}%` }}
      />

      {/* Content overlay */}
      <div className={cn('relative z-10 flex flex-col items-center justify-center h-full text-[10px]', textColor)}>
        {jobCount > 0 ? (
          <>
            <span className="font-bold text-xs">{cell.assigned_hours}h</span>
            <span className="opacity-75">{label}</span>
          </>
        ) : (
          <span className="text-ink-faint">—</span>
        )}
      </div>

      {/* Over-allocation indicator */}
      {pct > 100 && (
        <div className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-status-blocked-fg animate-pulse" />
      )}
    </button>
  );
}
